/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import { RadixSelectControl } from "../../../ui/radix-select";
import { setBasicDialogOpenFor, setConfirmDialog, setErrorMsg } from "../../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { sanitizeHtmlInput } from "../../../../utils/helpers";
import { constStr } from "../../../../globals/constants";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { useSelector } from "react-redux";
import { useAddEditAlarmForwarding } from "./hooks";
import { useStoreDispatch } from "../../../../store/hooks";
import { getProtocolList } from "../../../../pages/Administrations/helpers";
import { ISnmpGroup } from "../../../../store/api/admin/types";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { PasswordField } from "../../../Shared/Radix/Inputs/PasswordField";
import { Trash2 } from "lucide-react";

interface IAddEditAlarmForwardProps {
  selectedSnmpGroup?: ISnmpGroup;
  modelOpenMode?: string;
}

const AddEditAlarmForwarding: React.FC<IAddEditAlarmForwardProps> = ({ modelOpenMode = "Add", selectedSnmpGroup }) => {
  const dispatch = useStoreDispatch();
  const reduxErrMsg = useSelector(errorMsgSelector);
  const {
    formData,
    setFormData,
    errMsg,
    setErrMsg,
    version,
    setVersion,
    AuthenticationOptions,
    PrivacyOptions,
    isAuthChecked,
    setIsAuthChecked,
    isPrivChecked,
    setIsPrivChecked,
    submitHandler,
    isCreateSNMPGroup,
    authConfirmPassword,
    setAuthConfirmPassword,
    privConfirmPassword,
    setPrivConfirmPassword,
    onBlurHandler,
    authPassword,
    setAuthPassword,
    privPassword,
    setPrivPassword,
    handleDeleteSnmpGroup,
  } = useAddEditAlarmForwarding();

  const getValue = (val: string) => {
    return val?.includes(",");
  };

  const handleChange = (name: string, value: string) => {
    if (name === "ipAddressesList") {
      if (getValue(value)) {
        const newValue = value?.split(",");
        setFormData({ ...formData, [name]: newValue });
        setErrMsg({ ...errMsg, [name]: "" });
        return;
      }
    }
    setFormData({ ...formData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData({ ...formData, [name]: checked });
    if (name === "enabled" && !checked) {
      setErrMsg({ ...errMsg, ipAddressesList: "" });
    }
  };

  const handlePassChange = (name: string, value: string) => {
    if (name === "authPassword") setAuthPassword(value);
    else if (name === "privPassword") setPrivPassword(value);
    else if (name === "authConfirmPassword") {
      setAuthConfirmPassword(value);
      if (authPassword !== value) {
        setErrMsg({ ...errMsg, authConfirmPassword: "Passwords do not match" });
        dispatch(setErrorMsg({ ...errMsg, authConfirmPassword: "Passwords do not match" }));
      } else {
        setErrMsg({ ...errMsg, authConfirmPassword: "" });
        dispatch(setErrorMsg({ ...errMsg, authConfirmPassword: "" }));
      }
    } else if (name === "privConfirmPassword") {
      setPrivConfirmPassword(value);
      if (privPassword !== value) {
        setErrMsg({ ...errMsg, privConfirmPassword: "Passwords do not match" });
        dispatch(setErrorMsg({ ...errMsg, privConfirmPassword: "Passwords do not match" }));
      } else {
        setErrMsg({ ...errMsg, privConfirmPassword: "" });
        dispatch(setErrorMsg({ ...errMsg, privConfirmPassword: "" }));
      }
    }
  };

  const handlePasswordProtocol = (name: string) => {
    if (name === "privacyProtocol") setIsPrivChecked(!isPrivChecked);
    else setIsAuthChecked(!isAuthChecked);
  };

  const handleVersionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVersion(event.target.value);
    setErrMsg({});
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleClose = () => {
    dispatch(setErrorMsg({}));
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const authProtocolOptions = getProtocolList(AuthenticationOptions).map((o: any) => ({
    value: o.value,
    label: o.text,
  }));
  const privProtocolOptions = getProtocolList(PrivacyOptions).map((o: any) => ({
    value: o.value,
    label: o.text,
  }));

  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  const handleDelete = () => {
    setShowDeleteConfirm(true);
    dispatch(setConfirmDialog("SHEET_DELETE"));
  };

  const handleConfirmDelete = async (actionName: string) => {
    setShowDeleteConfirm(false);
    if (!actionName) return;
    if (actionName.match("delete") && formData?.name) {
      await handleDeleteSnmpGroup(sanitizeHtmlInput(formData.name), actionName);
      handleClose();
    }
  };

  const footer = (
    <div className="flex items-end justify-end w-full">
      <div className="flex items-end gap-2">
      {!isCreateSNMPGroup ? (
        <Button variant="outline" className="!text-destructive hover:!text-destructive !border-destructive" onClick={handleDelete}>
          <Trash2 className="h-4 w-4" fill="currentColor" />
          Delete
        </Button>
      ) : <div />}
      
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={submitHandler}>
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {showDeleteConfirm && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleConfirmDelete}>
          Are you sure you want to delete <strong>{sanitizeHtmlInput(formData?.name) || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      <Sheet open={true} onClose={handleClose} title={`${modelOpenMode} SNMP Managers Group`} footer={footer} width="512px">
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Name */}     
            <div className="w-3/4">
            <InputField
              label="Name"
              name="name"
              required
               autoFocus={true}
              disabled={!isCreateSNMPGroup}
              value={formData?.name}
              onChange={handleChange}
              onBlur={onBlurHandler}
              maxLength={100}
              placeholder="Enter name"
              tooltipText="Maximum length: 100 characters"            
            />
            </div>
            {!reduxErrMsg?.name && errMsg?.name && (
              <span className="text-xs text-destructive">{errMsg.name}</span>
            )}

          {/* Port */}
          <div className="w-1/6 pt-4">
          <InputField
            label="Port"
            name="port"
            type="number"
            required
            disabled
            value={formData?.port}
            onChange={handleChange}
            onBlur={onBlurHandler}
            placeholder="Enter port"
          />
          </div>
          {/* Enable */}
          <div className="flex items-center gap-2 pt-4">
            <Checkbox
              id="snmp-enabled"
              checked={formData?.enabled}
              onCheckedChange={(checked: boolean) => handleCheckboxChange("enabled", !!checked)}
            />
            <Label htmlFor="snmp-enabled" className="cursor-pointer">Enable</Label>
          </div>

          {/* Hostname */}   
          <div className="pt-4">
            <InputField
              label="Host Name"
              name="ipAddressesList"
              required
              disabled={!formData?.enabled}
              value={formData?.ipAddressesList}
              onChange={handleChange}
              onBlur={onBlurHandler}
              maxLength={255}
              placeholder="Host names or IP Addresses (separated by commas)"
              tooltipText="Maximum length: 255 characters"
            />
          </div>  
            {!reduxErrMsg?.ipAddressesList && errMsg?.ipAddressesList && (
              <span className="text-xs text-destructive">{errMsg.ipAddressesList}</span>
            )}
          
          {/* Enable heartbeat trap */}
          <div className="flex items-center gap-2 pt-4">
            <Checkbox
              id="snmp-heartbeat"
              checked={formData?.enableHeartbeat}
              onCheckedChange={(checked: boolean) => handleCheckboxChange("enableHeartbeat", !!checked)}
            />
            <Label htmlFor="snmp-heartbeat" className="cursor-pointer">Enable heartbeat trap</Label>
          </div>

          {/* Heartbeat Interval */}
          <div className="w-1/2 pt-4">
          <InputField
            label="Heartbeat Interval (Minutes)"
            name="heartbeatInterval"
            type="number"
            required
            value={formData?.heartbeatInterval}
            onChange={handleChange}
            onBlur={onBlurHandler}
            min={0}
            max={3600}
            placeholder="Enter heartbeat interval"
            tooltipText="Value should be between 0 and 3600"
          />
          </div>
          {/* SNMP Version */}
          <div className="space-y-2 pt-4">
            <Label>SNMP Version</Label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="snmpVersion"
                  value="V2C"
                  checked={version === "V2C"}
                  onChange={handleVersionChange}
                  style={{ accentColor: "var(--primary)" }}
                />
                V2C
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="snmpVersion"
                  value="V3"
                  checked={version === "V3"}
                  onChange={handleVersionChange}
                  style={{ accentColor: "var(--primary)" }}
                />
                V3
              </label>
            </div>
          </div>

          {/* V2C — Community */}
          {version === "V2C" ? (
            <div className="w-full pt-4">
              <InputField
                label="Community"
                name="community"
                value={formData?.community}
                onChange={handleChange}
                onBlur={onBlurHandler}
                maxLength={255}
                tooltipText="Maximum length: 255 characters"
                placeholder="Enter community"
              />
              {!reduxErrMsg?.community && errMsg?.community && (
                <p className="text-xs text-destructive mt-1">{errMsg.community}</p>
              )}
            </div>
          ) : (
            <>
              {/* V3 — Context Name */}
              <div className="w-2/3 pt-4">
              <InputField
                label="Context Name"
                name="contextName"
                value={formData?.contextName}
                onChange={handleChange}
                onBlur={onBlurHandler}
                maxLength={255}
                tooltipText="Maximum length: 255 characters"
                placeholder="Enter context name"
              />
              </div>
              {/* V3 — User Name */}
              <div className="w-2/3 pt-4">
                <InputField
                  label="User Name"
                  name="userName"
                  value={formData?.userName}
                  onChange={handleChange}
                  onBlur={onBlurHandler}
                  maxLength={255}
                  tooltipText="Maximum length: 255 characters"
                  placeholder="Enter user name"
                />
                {!reduxErrMsg?.userName && errMsg?.userName && (
                  <p className="text-xs text-destructive mt-1">{errMsg.userName}</p>
                )}
              </div>

              {/* V3 — Authentication Protocol */}
              <div className="space-y-2 w-2/3 pt-4">
                <Label htmlFor="snmp-auth-protocol" className="mb-4">Authentication Protocol</Label>
                <RadixSelectControl
                  value={formData?.authProtocol || "NO_AUTH"}
                  options={authProtocolOptions}
                  onValueChange={(value) => handleChange("authProtocol", value)}
                />
                {errMsg?.authProtocol && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.authProtocol}</p>}
              </div>

              {/* V3 — Change Auth Password */}
              <div className="flex items-center gap-2 pt-4">
                <Checkbox
                  id="snmp-auth-change"
                  checked={isAuthChecked}
                  onCheckedChange={() => handlePasswordProtocol("authProtocol")}
                />
                <Label htmlFor="snmp-auth-change" className="cursor-pointer">Change Password Authentication Protocol</Label>
              </div>

              {isAuthChecked && (
                <div className="space-y-4 pt-4">
                  <PasswordField
                    label="Password"
                    name="authPassword"
                    value={authPassword}
                    onChange={handlePassChange}
                    onBlur={onBlurHandler}
                  />
                  <PasswordField
                    label="Confirm Password"
                    name="authConfirmPassword"
                    value={authConfirmPassword}
                    onChange={handlePassChange}
                    onBlur={onBlurHandler}
                  />
                </div>
              )}

              {/* V3 — Privacy Protocol */}
              <div className="space-y-2 w-2/3 pt-4">
                <Label htmlFor="snmp-priv-protocol" className="mb-4">Privacy Protocol</Label>
                <RadixSelectControl
                  value={formData?.privProtocol || "NO_PRIV"}
                  options={privProtocolOptions}
                  onValueChange={(value) => handleChange("privProtocol", value)}
                />
                {errMsg?.privProtocol && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.privProtocol}</p>}
              </div>

              {/* V3 — Change Priv Password */}
              <div className="flex items-center gap-2 pt-4">
                <Checkbox
                  id="snmp-priv-change"
                  checked={isPrivChecked}
                  onCheckedChange={() => handlePasswordProtocol("privacyProtocol")}
                />
                <Label htmlFor="snmp-priv-change" className="cursor-pointer">Change Password Privacy Protocol</Label>
              </div>

              {isPrivChecked && (
                <div className="space-y-4 pt-4">
                  <PasswordField
                    label="Password"
                    name="privPassword"
                    value={privPassword}
                    onChange={handlePassChange}
                    onBlur={onBlurHandler}
                  />
                  <PasswordField
                    label="Confirm Password"
                    name="privConfirmPassword"
                    value={privConfirmPassword}
                    onChange={handlePassChange}
                    onBlur={onBlurHandler}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </form>
    </Sheet>
    </>
  );
};

export default AddEditAlarmForwarding;
